import React from "react";
import { useNavigate } from "react-router-dom";
import { Database, GitBranch, Users, Send, Cloud, Smartphone, Globe, Zap, MessageCircle, Mail, Bell } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";

// "Unify Profiles" hero card at the top of /cdp/unify — a 4-step summary of
// how identity resolution works, plus an animated hub-and-spoke diagram
// (left = raw sources, center = the resolved profile, right = activation
// channels) reusing the same fan-lines-into-a-hub technique as the
// Connections Overview screen's ConnectionFlow.
const STEPS = [
  {
    icon: <Database size={16} />,
    title: "Connect Data Sources",
    description: "Connect CRM, App events, web or telco data",
  },
  {
    icon: <GitBranch size={16} />,
    title: "Define Match Rules",
    description: "Define match rules through identifiers like MSISDN, Customer ID, Email etc",
  },
  {
    icon: <Users size={16} />,
    title: "Resolve Profiles",
    description: "Merge records into one live customer profile.",
  },
  {
    icon: <Send size={16} />,
    title: "Activate Profiles",
    description: "Explore profiles and push them to channels and tools.",
  },
];

const SOURCE_ICONS = [
  { icon: <Cloud size={13} />, label: "CRM" },
  { icon: <Smartphone size={13} />, label: "Telco" },
  { icon: <Globe size={13} />, label: "Web" },
  { icon: <Zap size={13} />, label: "Events" },
];

const DESTINATION_ICONS = [
  { icon: <MessageCircle size={13} />, label: "SMS" },
  { icon: <FaWhatsapp size={13} />, label: "WhatsApp" },
  { icon: <Mail size={13} />, label: "Email" },
  { icon: <Bell size={13} />, label: "Push" },
  { icon: <Cloud size={13} />, label: "CRM" },
];

// Shared row sizing so the SVG connector lines line up exactly with the
// icon rows regardless of how many are on each side.
const ROW_H = 30;

const IconDot: React.FC<{ icon: React.ReactNode; label: string }> = ({ icon, label }) => (
  <div className="flex items-center gap-2" style={{ height: ROW_H }}>
    <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 shrink-0">
      {icon}
    </span>
    <span className="text-[11px] text-gray-500 dark:text-gray-400 whitespace-nowrap">{label}</span>
  </div>
);

export const UnifyDiagram: React.FC = () => {
  const leftHeight = SOURCE_ICONS.length * ROW_H;
  const rightHeight = DESTINATION_ICONS.length * ROW_H;
  const svgHeight = Math.max(leftHeight, rightHeight);
  const leftOffset = (svgHeight - leftHeight) / 2;
  const rightOffset = (svgHeight - rightHeight) / 2;
  const hubY = svgHeight / 2;
  const leftRowY = (i: number) => leftOffset + i * ROW_H + ROW_H / 2;
  const rightRowY = (i: number) => rightOffset + i * ROW_H + ROW_H / 2;

  return (
    <div className="flex items-center" style={{ height: svgHeight }}>
      <style>{`
        @keyframes unify-hub-pulse {
          0%, 100% { transform: translate(-50%, -50%) scale(1); }
          50% { transform: translate(-50%, -50%) scale(1.03); }
        }
        .unify-hub-pulse { animation: unify-hub-pulse 2.6s ease-in-out infinite; }
      `}</style>

      {/* Left icon column */}
      <div className="flex flex-col justify-center shrink-0">
        {SOURCE_ICONS.map((s) => (
          <IconDot key={s.label} icon={s.icon} label={s.label} />
        ))}
      </div>

      {/* Connector lines + profile hub */}
      <div className="relative shrink-0" style={{ width: 220, height: svgHeight }}>
        <svg width="220" height={svgHeight} viewBox={`0 0 220 ${svgHeight}`} preserveAspectRatio="none" className="absolute inset-0">
          <defs>
            <linearGradient id="unifyLeftGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#4f46e5" stopOpacity="0.55" />
              <stop offset="100%" stopColor="#4f46e5" stopOpacity="0.25" />
            </linearGradient>
            <linearGradient id="unifyRightGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#4f46e5" stopOpacity="0.25" />
              <stop offset="100%" stopColor="#4f46e5" stopOpacity="0.55" />
            </linearGradient>
          </defs>

          {SOURCE_ICONS.map((s, i) => {
            const y = leftRowY(i);
            const id = `unify-src-${i}`;
            return (
              <g key={s.label}>
                <path id={id} d={`M 0 ${y} C 45 ${y}, 45 ${hubY}, 90 ${hubY}`} stroke="url(#unifyLeftGradient)" strokeWidth="1.5" fill="none" className="opacity-70" />
                <circle r="2.5" fill="#4f46e5" className="opacity-80">
                  <animateMotion dur="2.6s" repeatCount="indefinite" begin={`${i * 0.2}s`}>
                    <mpath href={`#${id}`} />
                  </animateMotion>
                </circle>
              </g>
            );
          })}

          {DESTINATION_ICONS.map((d, i) => {
            const y = rightRowY(i);
            const id = `unify-dst-${i}`;
            return (
              <g key={d.label}>
                <path id={id} d={`M 130 ${hubY} C 175 ${hubY}, 175 ${y}, 220 ${y}`} stroke="url(#unifyRightGradient)" strokeWidth="1.5" fill="none" className="opacity-70" />
                <circle r="2.5" fill="#4f46e5" className="opacity-80">
                  <animateMotion dur="2.6s" repeatCount="indefinite" begin={`${1.2 + i * 0.2}s`}>
                    <mpath href={`#${id}`} />
                  </animateMotion>
                </circle>
              </g>
            );
          })}
        </svg>

        {/* Resolved profile hub */}
        <div className="unify-hub-pulse absolute left-1/2" style={{ top: hubY }}>
          <div className="w-[112px] rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm p-2">
            <div className="flex items-center gap-1.5 mb-1">
              <span className="w-5 h-5 rounded-full bg-indigo-100 dark:bg-indigo-500/20 flex items-center justify-center text-indigo-600 dark:text-indigo-400 text-[9px] font-bold shrink-0">
                RS
              </span>
              <span className="text-[10px] font-semibold text-gray-900 dark:text-gray-100 truncate">Rahul Sharma</span>
            </div>
            <p className="text-[8.5px] text-[#7B7B7B] font-semibold dark:text-gray-500 leading-tight truncate">Customer ID: CUS-10492</p>
            <p className="text-[8.5px] text-[#7B7B7B] font-semibold dark:text-gray-500 leading-tight truncate">+91 98765 43210</p>
            <p className="text-[8.5px] text-[#7B7B7B] font-semibold dark:text-gray-500 leading-tight truncate">Bangalore, India</p>
            <p className="text-[8.5px] font-medium text-indigo-600 dark:text-indigo-400 mt-1">View Profile →</p>
          </div>
        </div>
      </div>

      {/* Right icon column */}
      <div className="flex flex-col justify-center shrink-0">
        {DESTINATION_ICONS.map((d) => (
          <IconDot key={d.label} icon={d.icon} label={d.label} />
        ))}
      </div>
    </div>
  );
};

export const UnifyHero: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5 sm:p-6 shadow-sm overflow-hidden">
      <h1 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-gray-100">Unify Profiles</h1>
      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-2xl">
        Resolve identities and create real-time unified customer profiles from CRM, telco and event data
      </p>

      <div className="mt-5 flex flex-col xl:flex-row xl:items-center gap-6">
        <div className="flex-1 min-w-0">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
            {STEPS.map((step) => (
              <div key={step.title} className="min-w-0">
                <div className="flex items-center gap-1.5 text-indigo-600 dark:text-indigo-400">
                  {step.icon}
                </div>
                <div className="mt-1.5 text-sm font-semibold text-gray-900 dark:text-gray-100">{step.title}</div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>

          <button
            type="button"
            // onClick={() => navigate("/cdp/connections/new")}
            className="mt-5 px-4 py-2.5 text-sm font-semibold text-white bg-[#4338CA] hover:bg-[#3730A3] rounded-lg transition-colors shadow-sm"
          >
            Start Unifying
          </button>
        </div>

        {/* Animated diagram — hidden on narrow screens where there's no room
            for it alongside the step list. */}
        <div className="hidden xl:block shrink-0 overflow-x-auto">
          <UnifyDiagram />
        </div>
      </div>
    </div>
  );
};

export default UnifyHero;
